import Col from "@visa/vds/col";
import Disclosure from '@visa/vds/disclosure';
import DisclosureContent from '@visa/vds/disclosure-content';
import DisclosureDetailsItem from '@visa/vds/disclosure-details-item';
import DisclosureHeader from '@visa/vds/disclosure-header';
import DisclosureHeaderWrapper from '@visa/vds/disclosure-header-wrapper';
import Row from "@visa/vds/row";
import React, { useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { EmailNotificationEvent, EmailNotificationInternalUserTypes, EmailNotificationSelectedUserNote, EmailNotificationService, EmailNotificationUnselectedUserNote } from "../../../../_constants/EmailNotificationConstants";
import { PageHelper } from "../../../../_helpers/PageHelper";
import { EmailNotificationApi } from '../../../../services/emailNotification.service';
import SelectedUserNote from './SelectedUserNote';
import SelectedUserTable from './SelectedUserTable';
import UnselectedUserNote from './UnselectedUserNote';
import UnselectedUserTable from './UnselectedUserTable';

const UserManagementSettings = (props) => {
    const { emailEvent, alertObj } = props;
    const promiseInProgress = usePromiseTracker();

    // Data states
    const createDefaultTableStates = () => {
        return {
            tableData: null,
            totalCount: 0,
            totalPage: 0,
            pageSize: 10,
            filter: {
                selectedPage: 1,
                sortBy: "organizationName",
                isDesc: false,
                search: "",
            }
        }
    }

    const [selectedUserTableState, setSelectedUserTableState] = useState(createDefaultTableStates());
    const [unselectedUserTableState, setUnselectedUserTableState] = useState(createDefaultTableStates());

    // Components states
    const [error, setError] = useState(null);
    const isInternalUser = EmailNotificationInternalUserTypes.has(emailEvent.userGroupType);
    const hasSelectedUserNote = !isInternalUser;
    const allowRemoveUser = isInternalUser;
    const showUnselectedUser = isInternalUser;
    const hasUnselectedUserNote = isInternalUser;

    const defaultExpandedPanels = [0];
    if (showUnselectedUser) defaultExpandedPanels.push(1);

    const getSelectedUserNote = () => {
        switch (emailEvent.id) {
            case EmailNotificationEvent.DafAcquirer: return <SelectedUserNote note={EmailNotificationSelectedUserNote.DafAcquirer} />;
            case EmailNotificationEvent.CertificationPackageStateUpdateExternal: return <SelectedUserNote note={EmailNotificationSelectedUserNote.ExternalUser} />;
            case EmailNotificationEvent.BaseVstsExpirationReminderExternal: return <SelectedUserNote note={EmailNotificationSelectedUserNote.ExternalUser} />;
            case EmailNotificationEvent.VstsExtensionExpirationReminderExternal: return <SelectedUserNote note={EmailNotificationSelectedUserNote.ExternalUser} />;
            case EmailNotificationEvent.ComplianceLetterExpirationReminderExternal: return <SelectedUserNote note={EmailNotificationSelectedUserNote.ExternalUser} />;
            default: return <></>;
        }
    }

    const getUnselectedUserNote = () => {
        if (!isInternalUser) return <></>;

        switch (emailEvent.serviceId) {
            case EmailNotificationService.Daf: return <UnselectedUserNote note={EmailNotificationUnselectedUserNote.Daf} />;
            case EmailNotificationService.Vsts: return <UnselectedUserNote note={EmailNotificationUnselectedUserNote.Vsts} />;
            case EmailNotificationService.VstsExpirationReminder: return <UnselectedUserNote note={EmailNotificationUnselectedUserNote.VstsExpirationReminder} />;
            case EmailNotificationService.ComplianceLetter: return <UnselectedUserNote note={EmailNotificationUnselectedUserNote.ComplianceLetter} />;
            default: return <></>;
        }
    }

    // fetch Data
    const unselectedUserFetchData = async (filter) => {
        let skipCount = PageHelper.GetSkipCount(unselectedUserTableState.pageSize, filter.selectedPage);
        trackPromise(
            EmailNotificationApi.GetUnselectedInternalEmailNotificationUsers(skipCount,
                filter.sortBy,
                filter.isDesc,
                unselectedUserTableState.pageSize,
                filter.search,
                emailEvent.id)
                .then((result) => {
                    var response = result.data;
                    setUnselectedUserTableState({
                        ...unselectedUserTableState,
                        tableData: response.items,
                        totalCount: response.totalCount,
                        totalPage: Math.ceil(response.totalCount / unselectedUserTableState.pageSize),
                        filter: filter
                    })
                })
                .catch((error) => {
                    if (alertObj)
                        alertObj.setErrorMessage(error.message);
                    else
                        setError(error);
                })
        );
    };

    const selectedUserFetchData = async (filter) => {
        let skipCount = PageHelper.GetSkipCount(selectedUserTableState.pageSize, filter.selectedPage);

        trackPromise(
            EmailNotificationApi.GetPagedSelectedEmailNotificationUsers(skipCount,
                filter.sortBy,
                filter.isDesc,
                selectedUserTableState.pageSize,
                filter.search,
                emailEvent.id)
                .then((result) => {
                    var response = result.data;
                    setSelectedUserTableState({
                        ...selectedUserTableState,
                        tableData: response.items,
                        totalCount: response.totalCount,
                        totalPage: Math.ceil(response.totalCount / selectedUserTableState.pageSize),
                        filter: filter
                    })
                })
                .catch((error) => {
                    if (alertObj)
                        alertObj.setErrorMessage(error.message);
                    else
                        setError(error);
                })
        );
    };

    const fetchData = () => {
        unselectedUserFetchData(unselectedUserTableState.filter, alertObj);
        selectedUserFetchData(selectedUserTableState.filter, alertObj);
    }

    // delete emailNotiticationUser
    const deleteEmailNotificationUser = async (selectedUser) => {
        trackPromise(
            EmailNotificationApi.DeleteEmailNotificationUser(selectedUser.id)
                .then(result => {
                    alertObj.setSuccessMessage("User successfully deleted to email notification event");
                    fetchDataPostDeleteEmailNotificationUser();
                }).catch((error) => {
                    alertObj.setErrorMessage("User failed to be deleted.");
                })
        );
    }

    const fetchDataPostDeleteEmailNotificationUser = () => {
        let previousPage = PageHelper.CountPreviousPageNumber(selectedUserTableState.filter.selectedPage, selectedUserTableState.totalCount, selectedUserTableState.pageSize, selectedUserTableState.totalPage);
        if (previousPage != selectedUserTableState.filter.selectedPage) {
            unselectedUserFetchData(unselectedUserTableState.filter, alertObj);
            selectedUserFetchData({ ...selectedUserTableState.filter, selectedPage: previousPage }, alertObj); // go to previous page
        }
        else
            fetchData();
    }

    // add emailNotiticationUser
    const addEmailNotificationUser = async (selectedUser) => {
        let emailNotificationUserDto = {
            volUserId: selectedUser.volUserId,
            eventId: emailEvent.id,
            frequency: emailEvent.frequency,
            receiveEmailNotifications: true,
            isSelectingVSSClients: false,
        };

        trackPromise(
            EmailNotificationApi.AddEmailNotificationUser(emailNotificationUserDto)
                .then(result => {
                    alertObj.setSuccessMessage("User successfully added to email notification event");
                    fetchDataPostAddEmailNotificationUser();
                }).catch(error => {
                    alertObj.setErrorMessage("User failed to be added.");
                })
        );
    }

    const fetchDataPostAddEmailNotificationUser = () => {
        let previousPage = PageHelper.CountPreviousPageNumber(unselectedUserTableState.filter.selectedPage, unselectedUserTableState.totalCount, unselectedUserTableState.pageSize, unselectedUserTableState.totalPage);
        if (previousPage != unselectedUserTableState.filter.selectedPage) {
            unselectedUserFetchData({ ...unselectedUserTableState.filter, selectedPage: previousPage }, alertObj); // go to previous page
            selectedUserFetchData(selectedUserTableState.filter, alertObj);
        }
        else
            fetchData();
    }

    React.useEffect(() => {
        fetchData();
    }, []);

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    return (
        <>
            <Row>
                <Col>
                    <Disclosure defaultExpandedId={defaultExpandedPanels}>
                        <DisclosureDetailsItem>
                            <DisclosureHeader style={{ backgroundColor: '#e2f6d5' }}>
                                <DisclosureHeaderWrapper>
                                    Members of {emailEvent.serviceName} email notification user group: {emailEvent.userGroupName}
                                </DisclosureHeaderWrapper>
                            </DisclosureHeader>
                            <DisclosureContent>
                                {hasSelectedUserNote &&
                                   getSelectedUserNote()
                                }
                                <SelectedUserTable
                                    fetchData={selectedUserFetchData}
                                    tableState={selectedUserTableState}
                                    allowRemoveUser={allowRemoveUser}
                                    handleClickAction={deleteEmailNotificationUser}
                                />
                            </DisclosureContent>
                        </DisclosureDetailsItem>
                        {showUnselectedUser ?
                            <DisclosureDetailsItem>
                                <DisclosureHeader>
                                    <DisclosureHeaderWrapper>
                                        Select Users for {emailEvent.userGroupName}
                                    </DisclosureHeaderWrapper>
                                </DisclosureHeader>
                                <DisclosureContent>
                                    {hasUnselectedUserNote &&
                                       getUnselectedUserNote()
                                    }
                                    <UnselectedUserTable
                                        fetchData={unselectedUserFetchData}
                                        tableState={unselectedUserTableState}
                                        handleClickAction={addEmailNotificationUser}
                                    />
                                </DisclosureContent>
                            </DisclosureDetailsItem>
                            : <></>
                        }
                    </Disclosure>
                </Col>
            </Row>
        </>
    );
};

export default UserManagementSettings;
